import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import style from './SideBar.module.css';
import { useSelector, useDispatch } from 'react-redux';
import { getUser } from '../../Redux/Actions/actions';
import { 
  FaHome, FaComments, FaChartBar, FaUsers, FaTags, FaRss, FaCalendarAlt, FaTools, FaClipboardList, FaUserPlus, FaCheckCircle, FaMapMarkerAlt, FaBalanceScale, FaRegCalendarPlus, FaRegCalendar
} from 'react-icons/fa';

const allowedEmails = [
  'arosa@byraices.com',
  'tsanesteban@byraices.com',
  'dsanesteban@byraices.com',
  'jgarcia@byraices.com'
];

const allowedAdmin = [
  'arosa@byraices.com',
  'dsanesteban@byraices.com',
];

const allowedListOnly = [
  'vsosa@byraices.com',
];

const allowedCreateEvent = [
  'arosa@byraices.com',
  'lalvarado@byraices.com'
];

const Sidebar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const sidebarRef = useRef(null);
  let startX = null;
  const dispatch = useDispatch();
  const userId = useSelector(state => state.userId);
  const user = useSelector(state => state.user);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: user,
          userId: user.id
        }
      });
    } else {
      if (userId) {
        dispatch(getUser(userId));
      }
    }
  }, [dispatch, userId]);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const showApproveButton = user && user.user && user.user.mail && allowedEmails.includes(user.user.mail);
  const showAllowedAdmin = user && user.user && user.user.mail && allowedAdmin.includes(user.user.mail);

  // Para móviles (touch)
  useEffect(() => {
    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
    };
    const handleTouchEnd = (e) => {
      if (startX === null) return;
      const endX = e.changedTouches[0].clientX;
      const diff = endX - startX;
      if (diff > 80) setIsExpanded(true); // Swipe derecha: abrir
      if (diff < -80) setIsExpanded(false); // Swipe izquierda: cerrar
      startX = null;
    };
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  // Para escritorio (mouse)
  useEffect(() => {
    let mouseDown = false;
    let mouseStartX = null;
    const handleMouseDown = (e) => {
      mouseDown = true;
      mouseStartX = e.clientX;
    };
    const handleMouseUp = (e) => {
      if (!mouseDown || mouseStartX === null) return;
      const diff = e.clientX - mouseStartX;
      if (diff > 80) setIsExpanded(true); // Arrastrar derecha: abrir
      if (diff < -80) setIsExpanded(false); // Arrastrar izquierda: cerrar
      mouseDown = false;
      mouseStartX = null;
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <div>
      <div className={`${style.sidebar} ${isExpanded ? style.expanded : ''}`} ref={sidebarRef}>
        <div className={style['sidebar-menu']}>
          <Link to="/properties" className={style.link}>
            <FaHome className={style.icon} />
            Propiedades
          </Link>
          <Link to="#" className={style.link}>
            <FaComments className={style.icon} />
            Consultas
          </Link>
          <Link to="#" className={style.link}>
            <FaChartBar className={style.icon} />
            Estadísticas
          </Link>
          <Link to="/clientes" className={style.link}>
            <FaUsers className={style.icon} />
            Clientes
          </Link>
          <Link to="/acm" className={style.link}>
            <FaTags className={style.icon} />
            ACM
          </Link>
          <Link to="#" className={style.link}>
            <FaRss className={style.icon} />
            Feed
          </Link>
          <Link to="/calendario" className={style.link}>
            <FaCalendarAlt className={style.icon} />
            Calendario
          </Link>
          {allowedCreateEvent.includes(user?.user?.mail) && (
            <Link to="/eventos/crear" className={style.link}>
              <FaRegCalendarPlus className={style.icon} />
              Crear Evento
            </Link>
          )}
          <Link to="/placas" className={style.link}>
            <FaTools className={style.icon} />
            Placas
          </Link>
          <Link to="#" className={style.link}>
            <FaClipboardList className={style.icon} />
            Reportes
          </Link>
          {showApproveButton && (
            <Link to="/aprobar" className={style.link}>
              <FaCheckCircle className={style.icon} />
              Aprobar
            </Link>
          )}
          {showApproveButton && (
            <Link to="/visitas" className={style.link}>
              <FaMapMarkerAlt className={style.icon} />
              Visitas
            </Link>
          )}
          {showApproveButton && (
            <Link to="/usuarios" className={style.link}>
              <FaUserPlus className={style.icon} />
              Usuarios
            </Link>
          )}
          <Link to="/alquileres" className={style.link}>
            <FaHome className={style.icon} />
            Alquileres
          </Link>
          {(showApproveButton || (user?.user?.mail && allowedListOnly.includes(user.user.mail))) && (
            <Link to="/lista-administrador" className={style.link}>
              <FaClipboardList className={style.icon} />
              Lista
            </Link>
          )}
          {(showApproveButton || (user?.user?.mail && allowedListOnly.includes(user.user.mail))) && (
            <Link to="/dashboard-sesiones" className={style.link}>
              <FaClipboardList className={style.icon} />
              Sesiones
            </Link>
          )}
          {showAllowedAdmin && (
            <Link to="/balance" className={style.link}>
              <FaBalanceScale className={style.icon} />
              Balance
            </Link>
          )}
        </div>
        {user && user.user && (
          <div className={style['user-details']}>
            {user.user.photo && <img src={user.user.photo} alt="User Photo" />}
            <p>{user.user.name} {user.user.last_name}</p>
          </div>
        )}
      </div>
      {isExpanded && (
        <div
          className={style.overlay}
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
};

export default Sidebar;
